import { useEntityRecords } from '@wordpress/core-data';
import { useState, useEffect, useMemo } from '@wordpress/element';

const BASE_QUERY = {
  order: 'asc',
  _fields: 'id,name,meta',
  context: 'view',
};

/**
 * Custom hook to fetch and manage term autocomplete options.
 *
 * @param {string}               taxonomySlug    The taxonomy slug
 * @param {string}               search          The search query
 * @param {Array<number>|number} existingTermIds The existing term IDs
 *
 * @return {UseTermAutocompleteState}                 The term loading state, autocomplete terms, and selected terms.
 */
function useTermAutocomplete(taxonomySlug, search, existingTermIds) {
  const [terms, setTerms] = useState([]);
  const [selectedTerms, setSelectedTerms] = useState([]);

  const existingIds = useMemo(() => {
    if (Array.isArray(existingTermIds)) {
      return existingTermIds.map((id) => parseInt(id, 10));
    }

    if (!existingTermIds) {
      return [];
    }

    return [parseInt(existingTermIds, 10)];
  }, [existingTermIds]);

  /**
   * Fetches term objects based on the existing term IDs.
   *
   * @type {{ records: Array<{ id: number, name: string }>, isResolving: boolean }}
   */
  const existingTerms = useEntityRecords('taxonomy', taxonomySlug, {
    ...BASE_QUERY,
    include: existingIds,
    orderby: 'include',
  });

  /**
   * Fetches term objects based on the search query.
   *
   * @type {{ records: Array<{ id: number, name: string }>, isResolving: boolean }}
   */
  const searchResults = useEntityRecords('taxonomy', taxonomySlug, {
    ...BASE_QUERY,
    search,
    orderby: 'name',
    exclude: existingIds,
  });

  /**
   * Updates the selected terms value after the existing terms are resolved.
   * Also validates the selected terms to ensure they are valid terms.
   *
   * This is required to ensure that the selected terms are displayed correctly, as most
   * fields require more than the term ID to be displayed correctly.
   */
  useEffect(() => {
    if (existingTerms.records && !existingTerms.isResolving) {
      const newSelectedTerms = existingIds?.reduce((acc, id) => {
        const foundTerm = existingTerms.records?.find((term) => term.id === id);

        if (foundTerm) {
          acc.push(foundTerm);
        }

        return acc;
      }, []);

      setSelectedTerms(newSelectedTerms || []);
    }
  }, [existingIds, existingTerms.records, existingTerms.isResolving, setSelectedTerms]);

  /**
   * Updates the terms value after the search results are resolved.
   *
   * The terms are a combination of the search results and the existing terms,
   * which is required so that the selected terms can be displayed.
   */
  useEffect(() => {
    const newTerms = [];
    if (searchResults.records && !searchResults.isResolving) {
      searchResults.records.forEach((term) => {
        newTerms.push(term);
      });
    }
    if (existingTerms.records && !existingTerms.isResolving) {
      existingTerms.records.forEach((term) => {
        newTerms.push(term);
      });
    }
    setTerms(newTerms);
  }, [
    searchResults.records,
    searchResults.isResolving,
    existingTerms.records,
    existingTerms.isResolving,
    setTerms,
  ]);

  const isLoading = existingTerms.isResolving || searchResults.isResolving;

  return { isLoading, terms, selectedTerms };
}

export default useTermAutocomplete;
